/**
 * Product Variant Table JS
 * Handles variant selection and direct add-to-cart from the table.
 *
 * For products with >250 variants (data-needs-load-all="true") the section
 * server-renders only the first page (50 rows, Liquid's product.variants cap).
 * This script then:
 *   1. Loads the remaining pages via the Section Rendering API.
 *   2. Sorts/groups the full set client-side (mirroring the section settings).
 *   3. Rebuilds the filter dropdowns from the complete data set.
 *   4. Adds a search box, a result count, and client-side pagination.
 *   5. Publishes the full variant matrix (variant-table:loaded) so the main
 *      picker can fix its >250 cascade filtering.
 * Products with <=250 variants keep the original (all-rows, no-pagination) behaviour.
 */

// Shared measurement (Inch/Metric) state, synced with the top variant picker (product-info.js).
// Guarded singleton so load order between the two scripts is moot.
window.WPMeasurement = window.WPMeasurement || {
    value: null,
    init: function (v) { if (this.value == null && v != null) this.value = v; },
    set: function (v) {
        if (v == null || v === this.value) return;
        this.value = v;
        document.dispatchEvent(new CustomEvent('wp:measurement', { detail: { value: v } }));
    },
    get: function () { return this.value; },
};

(function () {
    var CONCURRENCY = 6;
    var MEASUREMENT_KEY = 'measurement';

    // Canonical option ordering lives in assets/variant-sort.js so the table and
    // the top picker can never disagree. See that file for why an option without
    // a canonical order must be left in its stored order rather than sorted
    // alphabetically.
    var VS = window.WPVariantSort;
    var naturalCompare = VS.naturalCompare;
    var normalize = VS.normalize;

    function initVariantTable() {
        var container = document.querySelector('.product-variant-table-container');
        if (!container) return;

        var table = container.querySelector('.product-variant-table');
        var tbody = table ? table.querySelector('tbody') : null;
        if (!tbody) return;

        var wrapper = container.closest('.product-variant-table-wrapper');
        var filtersContainer = wrapper ? wrapper.querySelector('.variant-table-filters') : null;
        var noResults = tbody.querySelector('.variant-table__no-results');

        var needsLoadAll = container.dataset.needsLoadAll === 'true';
        var pageSize = parseInt(container.dataset.pageSize, 10) || 50;

        // Option names by 1-based index (e.g. ["Size","Material","Member"]), used to
        // sort each option by its meaning rather than plain alphabetical order.
        var optionNames = (container.dataset.optionNames || '').split('|').map(function (s) { return s.trim(); });
        function optionNameForKey(optKey) { // 'option1' -> optionNames[0]
            var idx = parseInt(String(optKey).replace('option', ''), 10);
            return (idx && optionNames[idx - 1]) || '';
        }

        // Live row accessor — the row set grows after load-all, so never cache it.
        function getRows() {
            return Array.prototype.slice.call(tbody.querySelectorAll('.variant-row'));
        }

        // How many variant cards a phone shows before "Show more". Declared above
        // `state` because the state literal seeds mobileShown from it.
        var MOBILE_PAGE = 25;

        // Shared state for the paginated (>250) experience.
        var state = {
            currentPage: 1,
            filteredRows: [],
            searchTerm: '',
            toggles: {}, // metafield key -> active value
            toggleDefaults: {}, // metafield key -> default value (for Clear All)
            mobileShown: MOBILE_PAGE, // rows revealed on phones; see applyMobileCap
        };

        // ---- Mobile "Show more" cap (< 750px only) ----
        //
        // On a phone the table renders as stacked cards (see the max-width: 749px
        // block in product-variant-table.css), which fixes usability but not length:
        // a 207-variant gage still runs past 20,000px of scroll. This reveals 25 at
        // a time.
        //
        // It layers ON TOP of the two existing visibility classes rather than
        // replacing either of them:
        //   .is-filtered   — set by applyFilters(), row does not match the filters
        //   .is-paged-out  — set by renderPage(), row is not on the current page
        //                    (>250-variant products only)
        // A row is a candidate for the cap only if it survives both, so filtering,
        // sorting, group-by and the desktop pager all keep working untouched and
        // operate on the full row set. Nothing is removed from the DOM.
        //
        // DESKTOP SAFETY — two independent gates, either one sufficient:
        //   1. every code path here returns early unless mobileMQ matches, and it
        //      strips the class on the way out, so a resize to desktop restores
        //      every row;
        //   2. `.is-mobile-capped { display: none }` is itself declared only inside
        //      the max-width: 749px block, so even a stray class is inert >=750px.
        var mobileMQ = window.matchMedia('(max-width: 749px)');
        var mobileMoreBtn = null;

        function ensureMobileMoreBtn() {
            if (mobileMoreBtn) return mobileMoreBtn;
            mobileMoreBtn = document.createElement('button');
            mobileMoreBtn.type = 'button';
            mobileMoreBtn.className = 'variant-table__show-more';
            mobileMoreBtn.style.display = 'none';
            mobileMoreBtn.addEventListener('click', function () {
                state.mobileShown += MOBILE_PAGE;
                applyMobileCap(false);
            });
            container.parentNode.insertBefore(mobileMoreBtn, container.nextSibling);
            return mobileMoreBtn;
        }

        function applyMobileCap(reset) {
            var btn = ensureMobileMoreBtn();
            var rows = getRows();

            if (!mobileMQ.matches) {
                rows.forEach(function (row) { row.classList.remove('is-mobile-capped'); });
                btn.style.display = 'none';
                return;
            }

            if (reset) state.mobileShown = MOBILE_PAGE;

            var shownCount = 0;
            var hiddenCount = 0;
            rows.forEach(function (row) {
                // Rows already hidden by a filter or by the pager are not counted —
                // the cap applies to what the visitor would otherwise be seeing.
                if (row.classList.contains('is-filtered') || row.classList.contains('is-paged-out')) {
                    row.classList.remove('is-mobile-capped');
                    return;
                }
                if (shownCount < state.mobileShown) {
                    row.classList.remove('is-mobile-capped');
                    shownCount++;
                } else {
                    row.classList.add('is-mobile-capped');
                    hiddenCount++;
                }
            });

            if (hiddenCount > 0) {
                var next = Math.min(MOBILE_PAGE, hiddenCount);
                btn.textContent = 'Show ' + next + ' more (' + hiddenCount + ' remaining)';
                btn.style.display = 'block';
            } else {
                btn.style.display = 'none';
            }
        }

        // Rotating a phone to landscape, or resizing across the breakpoint on a
        // desktop browser, re-evaluates the cap in both directions.
        var mqHandler = function () { applyMobileCap(true); };
        if (mobileMQ.addEventListener) mobileMQ.addEventListener('change', mqHandler);
        else if (mobileMQ.addListener) mobileMQ.addListener(mqHandler);

        // ---- One action per row (Select XOR Add to cart) ----
        // Every row gets exactly one button — never both.
        //   - "needs config" row (a required custom input applies, OR it's an add-on /
        //     call-for-quote variant with no inline form): keep "Select" so the customer is
        //     sent to the top picker to configure / request a quote. Strip any inline form.
        //   - purchasable, nothing-to-configure row: drop the redundant "Select" and keep the
        //     inline "Add to cart".
        // Required custom inputs (e.g. "Class") live ONLY in the top picker's form, not in the
        // table's per-row mini-form, so an inline "Add to cart" on such a row would bypass the
        // requirement. Mirror the picker's matching logic (assets/product-info.js#updateCustomInputs).
        function applyConfigRequirement() {
            var requiresAll = false;
            var requiredValues = {}; // normalized option value -> true

            var productInfo = document.querySelector('product-info');
            if (productInfo) {
                var wrappers = productInfo.querySelectorAll('.custom-input-wrapper');
                Array.prototype.forEach.call(wrappers, function (wrapper) {
                    // Required is marked by the asterisk in the label (same test product-info.js uses).
                    if (!wrapper.querySelector('.form__label span[aria-hidden="true"]')) return;

                    var mapping = wrapper.dataset.variantMapping;
                    if (!mapping || !mapping.trim()) {
                        // No variant mapping => the input applies to every variant on the product.
                        requiresAll = true;
                        return;
                    }
                    mapping.split(',').forEach(function (v) {
                        var n = normalize(v);
                        if (n) requiredValues[n] = true;
                    });
                });
            }

            var hasRequiredInputs = requiresAll || Object.keys(requiredValues).length > 0;

            getRows().forEach(function (row) {
                var form = row.querySelector('.variant-actions product-form');

                var needsConfig = requiresAll;
                if (!needsConfig && hasRequiredInputs) {
                    var opts = [row.dataset.option1, row.dataset.option2, row.dataset.option3];
                    needsConfig = opts.some(function (o) {
                        var n = normalize(o);
                        return n && requiredValues[n];
                    });
                }

                if (needsConfig) {
                    // Configuration required: keep "Select", remove the bypassing inline form.
                    if (form) form.parentNode.removeChild(form);
                    return;
                }

                // Nothing to configure. If the row is purchasable (has an inline form), drop the
                // redundant "Select". Otherwise (add-on / call-for-quote) "Select" is the sole action.
                if (form) {
                    var selectBtn = row.querySelector('.variant-actions .variant-select-btn');
                    if (selectBtn) selectBtn.parentNode.removeChild(selectBtn);
                }
            });
        }

        // ---- Variant selection (delegated; works for any row in the DOM) ----
        container.addEventListener('click', function (event) {
            var selectBtn = event.target.closest('.variant-select-btn');
            if (selectBtn) {
                selectVariant(selectBtn.dataset.variantId);
            }
        });

        // ---- Filtering (shared by both experiences) ----
        function rowMatchesFilters(row, filters) {
            for (var i = 0; i < filters.length; i++) {
                var filter = filters[i];
                var val = filter.value;
                if (!val) continue;
                if (filter.dataset.optionIndex) {
                    if (row.getAttribute('data-option' + filter.dataset.optionIndex) !== val) return false;
                } else if (filter.dataset.metafieldKey) {
                    if (row.getAttribute('data-meta-' + filter.dataset.metafieldKey) !== val) return false;
                }
            }
            return true;
        }

        function rowMatchesSearch(row, term) {
            if (!term) return true;
            return (row.textContent || '').toLowerCase().indexOf(term) !== -1;
        }

        function rowMatchesToggles(row) {
            for (var key in state.toggles) {
                var val = state.toggles[key];
                if (!val) continue;
                if (row.getAttribute('data-meta-' + key) !== val) return false;
            }
            return true;
        }

        var filters = filtersContainer
            ? Array.prototype.slice.call(filtersContainer.querySelectorAll('.variant-filter-select'))
            : [];
        var resetBtn = filtersContainer ? filtersContainer.querySelector('.variant-filter-reset') : null;

        function applyFilters() {
            var rows = getRows();
            var term = state.searchTerm;
            var matched = [];

            rows.forEach(function (row) {
                var ok = rowMatchesFilters(row, filters) && rowMatchesSearch(row, term) && rowMatchesToggles(row);
                if (ok) {
                    row.classList.remove('is-filtered');
                    matched.push(row);
                } else {
                    row.classList.add('is-filtered');
                }
            });

            if (resetBtn) {
                var anyToggle = Object.keys(state.toggles).some(function (k) { return state.toggles[k]; });
                var anyActive = filters.some(function (f) { return f.value; }) || !!term || anyToggle;
                resetBtn.style.display = anyActive ? 'block' : 'none';
            }

            if (needsLoadAll) {
                state.filteredRows = matched;
                state.currentPage = 1;
                renderPage();
            } else if (noResults) {
                noResults.style.display = (matched.length === 0) ? 'table-row' : 'none';
            }

            // Filters changed: restart the mobile cap at the top of the new result set.
            applyMobileCap(true);
        }

        filters.forEach(function (filter) {
            filter.addEventListener('change', applyFilters);
        });
        if (resetBtn) {
            resetBtn.addEventListener('click', function () {
                filters.forEach(function (filter) { filter.value = ''; });
                state.searchTerm = '';
                // Toggles have no "All" — Clear All restores each toggle to its default.
                Object.keys(state.toggleDefaults).forEach(function (k) {
                    if (k === MEASUREMENT_KEY) {
                        window.WPMeasurement.set(state.toggleDefaults[k]);
                        state.toggles[k] = state.toggleDefaults[k];
                    } else {
                        state.toggles[k] = state.toggleDefaults[k];
                    }
                });
                syncToggleButtons();
                applyFilters();
            });
        }

        // Two-way sync: the top picker (or the other toggle) changing the unit updates this filter.
        document.addEventListener('wp:measurement', function (event) {
            var v = event.detail && event.detail.value;
            if (v == null) return;
            if (!(MEASUREMENT_KEY in state.toggleDefaults)) return; // no measurement toggle on this table
            state.toggles[MEASUREMENT_KEY] = v;
            syncToggleButtons();
            rebuildFilterOptions(); // re-cascade dependent option filters (e.g. Size) to this unit
            applyFilters();
        });

        // ---- Inch/Metric (and other configured) segmented toggles ----
        var toggleKeys = (container.dataset.toggleKeys || '')
            .split(',')
            .map(function (k) { return k.trim(); })
            .filter(Boolean);

        function humanize(key) {
            return key.replace(/_/g, ' ').replace(/\b\w/g, function (c) { return c.toUpperCase(); });
        }

        // Ensure a filter row exists (some products may have filtering disabled but still
        // want the toggle). Reassigns the captured filtersContainer if it has to create one.
        function ensureFilterRow() {
            if (filtersContainer) return filtersContainer;
            if (!wrapper) return null;
            filtersContainer = document.createElement('div');
            filtersContainer.className = 'variant-table-filters';
            wrapper.insertBefore(filtersContainer, container);
            return filtersContainer;
        }

        // Build a segmented control per toggle key from the values present in the (fully
        // loaded) row set, inserted as the FIRST item(s) in the filter row. Only renders
        // when a key has 2+ distinct values. Idempotent (clears prior toggle groups first).
        function buildToggles() {
            if (!toggleKeys.length) return;
            var row = ensureFilterRow();
            if (!row) return;

            Array.prototype.forEach.call(row.querySelectorAll('.variant-toggle-filter-group'), function (el) {
                el.remove();
            });

            var frag = document.createDocumentFragment();
            toggleKeys.forEach(function (key) {
                var seen = {};
                var values = [];
                getRows().forEach(function (r) {
                    var v = r.getAttribute('data-meta-' + key);
                    if (v && !seen[v]) { seen[v] = true; values.push(v); }
                });
                if (values.length < 2) return; // single-unit product: no toggle needed
                values.sort(naturalCompare);

                // No "All" option for measurement; default to Inch (synced via the shared bus).
                if (key === MEASUREMENT_KEY) {
                    window.WPMeasurement.init(values.indexOf('Inch') >= 0 ? 'Inch' : values[0]);
                    state.toggles[key] = window.WPMeasurement.get();
                } else if (!(key in state.toggles)) {
                    state.toggles[key] = values[0];
                }
                state.toggleDefaults[key] = state.toggles[key];

                var group = document.createElement('div');
                group.className = 'variant-filter-group variant-toggle-filter-group';
                group.dataset.toggleKey = key;

                var label = document.createElement('label');
                label.className = 'filter-label';
                label.textContent = humanize(key);
                group.appendChild(label);

                var seg = document.createElement('div');
                seg.className = 'variant-toggle-group';
                seg.setAttribute('role', 'group');
                seg.setAttribute('aria-label', humanize(key));

                var buttons = values.map(function (v) { return { label: v, value: v }; });
                buttons.forEach(function (b) {
                    var btn = document.createElement('button');
                    btn.type = 'button';
                    btn.className = 'variant-toggle-btn';
                    btn.dataset.value = b.value;
                    btn.textContent = b.label;
                    if (state.toggles[key] === b.value) btn.classList.add('variant-toggle-btn--active');
                    seg.appendChild(btn);
                });

                seg.addEventListener('click', function (event) {
                    var btn = event.target.closest('.variant-toggle-btn');
                    if (!btn) return;
                    if (key === MEASUREMENT_KEY) {
                        window.WPMeasurement.set(btn.dataset.value); // bus event drives state + filtering
                    } else {
                        state.toggles[key] = btn.dataset.value;
                        syncToggleButtons();
                        applyFilters();
                    }
                });

                group.appendChild(seg);
                frag.appendChild(group);
            });

            row.insertBefore(frag, row.firstChild);
        }

        function syncToggleButtons() {
            if (!filtersContainer) return;
            Array.prototype.forEach.call(filtersContainer.querySelectorAll('.variant-toggle-filter-group'), function (group) {
                var key = group.dataset.toggleKey;
                Array.prototype.forEach.call(group.querySelectorAll('.variant-toggle-btn'), function (btn) {
                    btn.classList.toggle('variant-toggle-btn--active', btn.dataset.value === (state.toggles[key] || ''));
                });
            });
        }

        // ---- Paginated experience (>250 only) ----

        function renderPage() {
            var total = state.filteredRows.length;
            var totalPages = Math.max(1, Math.ceil(total / pageSize));
            if (state.currentPage > totalPages) state.currentPage = totalPages;
            var start = (state.currentPage - 1) * pageSize;
            var end = start + pageSize;

            // Hide everything not on the current page; matched rows already lack .is-filtered.
            getRows().forEach(function (row) {
                row.classList.add('is-paged-out');
            });
            for (var i = start; i < end && i < total; i++) {
                state.filteredRows[i].classList.remove('is-paged-out');
            }

            if (noResults) noResults.style.display = (total === 0) ? 'table-row' : 'none';
            updateCount(start, Math.min(end, total), total);
            renderPagerControls(totalPages);
            applyMobileCap(true);
        }

        var countEl = null;
        function updateCount(startIdx, endIdx, total) {
            if (!countEl) return;
            var grandTotal = parseInt(container.dataset.variantsCount, 10) || getRows().length;
            if (total === 0) {
                countEl.textContent = 'No variants match your search.';
            } else if (total === grandTotal) {
                countEl.textContent = 'Showing ' + (startIdx + 1) + '–' + endIdx + ' of ' + total + ' variants';
            } else {
                countEl.textContent = 'Showing ' + (startIdx + 1) + '–' + endIdx + ' of ' + total +
                    ' matching variants (' + grandTotal + ' total)';
            }
        }

        var pagerEl = null;
        function renderPagerControls(totalPages) {
            if (!pagerEl) return;
            if (totalPages <= 1) { pagerEl.innerHTML = ''; return; }

            var cur = state.currentPage;
            var parts = [];
            parts.push(pagerButton('‹ Prev', cur - 1, cur === 1));

            // Compact window of page numbers around the current page.
            var windowSize = 2;
            var pages = [];
            pages.push(1);
            for (var p = cur - windowSize; p <= cur + windowSize; p++) {
                if (p > 1 && p < totalPages) pages.push(p);
            }
            if (totalPages > 1) pages.push(totalPages);
            pages = pages.filter(function (v, i, arr) { return arr.indexOf(v) === i; }).sort(function (a, b) { return a - b; });

            var last = 0;
            pages.forEach(function (p) {
                if (last && p - last > 1) parts.push('<span class="variant-pager__ellipsis">…</span>');
                parts.push(pagerButton(String(p), p, false, p === cur));
                last = p;
            });

            parts.push(pagerButton('Next ›', cur + 1, cur === totalPages));
            pagerEl.innerHTML = parts.join('');
        }

        function pagerButton(label, page, disabled, active) {
            var cls = 'variant-pager__btn';
            if (active) cls += ' variant-pager__btn--active';
            return '<button type="button" class="' + cls + '"' +
                (disabled ? ' disabled' : '') +
                ' data-page="' + page + '">' + label + '</button>';
        }

        // ---- Sorting / grouping ----
        function optionValue(row, optionKey) {
            if (optionKey === 'option1') return row.getAttribute('data-option1') || '';
            if (optionKey === 'option2') return row.getAttribute('data-option2') || '';
            if (optionKey === 'option3') return row.getAttribute('data-option3') || '';
            return '';
        }

        // One comparator per option column, built from that column's complete
        // value set (Size ordering depends on the whole set — see variant-sort.js).
        //
        // A column with no canonical order still gets a comparator: one that ranks
        // values by where they first appear in the incoming rows. It has to stay a
        // real sort key. Returning 0 instead makes the column transparent, and a
        // later column then reorders rows straight across its values — that is what
        // flipped ACME's Size > Class > Member nesting into Size > Member > Class,
        // because its 2G/3G/4G grades aren't in the canonical Class order.
        function buildColumnComparators(rows) {
            var cmps = {}, anyCanonical = false;
            ['option1', 'option2', 'option3'].forEach(function (k) {
                var name = optionNameForKey(k);
                if (!name) { cmps[k] = null; return; }

                var seen = {}, values = [];
                rows.forEach(function (r) {
                    var v = optionValue(r, k);
                    if (v && !seen[v]) { seen[v] = true; values.push(v); }
                });

                var cmp = VS.makeComparator(name, values);
                if (cmp) {
                    anyCanonical = true;
                    cmps[k] = cmp;
                    return;
                }
                var order = {};
                values.forEach(function (v, i) { order[v] = i; });
                cmps[k] = function (a, b) {
                    var ia = order[a] == null ? Infinity : order[a];
                    var ib = order[b] == null ? Infinity : order[b];
                    return ia === ib ? 0 : ia - ib;
                };
            });
            return { cmps: cmps, anyCanonical: anyCanonical };
        }

        function sortAllRows() {
            var groupBy = container.dataset.groupBy;
            var subGroupBy = container.dataset.subGroupBy;
            var sortOrder = container.dataset.sortOrder;
            var rows = getRows();
            var built = buildColumnComparators(rows);
            var cmps = built.cmps;

            // Nothing to order canonically (e.g. a Style/Model-only product):
            // leave the server-rendered order strictly alone.
            if (!built.anyCanonical && sortOrder !== 'price_asc' && sortOrder !== 'price_desc'
                && sortOrder !== 'alphabetical' && sortOrder !== 'reverse_alphabetical') {
                return;
            }

            function cmpCol(k, a, b) {
                var fn = cmps[k];
                return fn ? fn(optionValue(a, k), optionValue(b, k)) : 0;
            }

            var allOptKeys = ['option1', 'option2', 'option3'];
            // Array.prototype.sort is stable, so columns with no canonical order
            // (cmpCol returns 0) fall back to the incoming DOM order rather than
            // being rearranged alphabetically.
            rows.sort(function (a, b) {
                var c;
                // 1. group-by column
                if (groupBy && groupBy !== 'none') {
                    c = cmpCol(groupBy, a, b);
                    if (c !== 0) return c;
                }
                // 2. sub-group-by column
                if (subGroupBy && subGroupBy !== 'none' && subGroupBy !== groupBy) {
                    c = cmpCol(subGroupBy, a, b);
                    if (c !== 0) return c;
                }
                // 3. explicit price sort
                if (sortOrder === 'price_asc' || sortOrder === 'price_desc') {
                    var pa = parseFloat(a.getAttribute('data-price')) || 0;
                    var pb = parseFloat(b.getAttribute('data-price')) || 0;
                    c = pa - pb;
                    if (sortOrder === 'price_desc') c = -c;
                    if (c !== 0) return c;
                }
                // 4. remaining option columns (so Member still reads GO, NO GO,
                //    GO/NO GO w/ Handle even when it isn't the group column)
                for (var i = 0; i < allOptKeys.length; i++) {
                    var k = allOptKeys[i];
                    if (k === groupBy || k === subGroupBy) continue;
                    c = cmpCol(k, a, b);
                    if (c !== 0) return c;
                }
                // 5. title fallback — only when the merchant asked for a title sort
                if (sortOrder === 'alphabetical' || sortOrder === 'reverse_alphabetical') {
                    c = naturalCompare(a.getAttribute('data-title') || '', b.getAttribute('data-title') || '');
                    return (sortOrder === 'reverse_alphabetical') ? -c : c;
                }
                return 0;
            });

            // Re-insert in sorted order (before the no-results row).
            var frag = document.createDocumentFragment();
            rows.forEach(function (row) { frag.appendChild(row); });
            if (noResults) {
                tbody.insertBefore(frag, noResults);
            } else {
                tbody.appendChild(frag);
            }
        }

        // ---- Rebuild filter dropdowns from the complete (loaded) data set ----
        function rebuildFilterOptions() {
            var meas = state.toggles[MEASUREMENT_KEY];
            filters.forEach(function (select) {
                var attr, optName = null, isOption = false;
                if (select.dataset.optionIndex) {
                    attr = 'data-option' + select.dataset.optionIndex;
                    optName = optionNameForKey('option' + select.dataset.optionIndex);
                    isOption = true;
                } else if (select.dataset.metafieldKey) {
                    attr = 'data-meta-' + select.dataset.metafieldKey;
                } else {
                    return;
                }
                var current = select.value;
                var seen = {};
                var values = [];
                getRows().forEach(function (row) {
                    // Cascade: option filters (e.g. Size) only list values available for
                    // the selected measurement — mirrors the top picker's dependent dropdowns.
                    if (isOption && meas && row.getAttribute('data-meta-' + MEASUREMENT_KEY) !== meas) return;
                    var v = row.getAttribute(attr);
                    if (v && !seen[v]) { seen[v] = true; values.push(v); }
                });
                if (isOption && optName) {
                    values = VS.sortValues(optName, values); // same array back when not sortable
                } else {
                    values.sort(naturalCompare);
                }

                var allOption = select.querySelector('option[value=""]');
                var allLabel = allOption ? allOption.textContent : 'All';
                select.innerHTML = '';
                var first = document.createElement('option');
                first.value = '';
                first.textContent = allLabel;
                select.appendChild(first);
                values.forEach(function (v) {
                    var opt = document.createElement('option');
                    opt.value = v;
                    opt.textContent = v;
                    select.appendChild(opt);
                });
                if (current && seen[current]) select.value = current;
            });
        }

        // ---- Build the count + pager scaffolding (>250 only) ----
        function buildEnhancedUI() {
            // Result count.
            countEl = document.createElement('div');
            countEl.className = 'variant-table__count';
            container.parentNode.insertBefore(countEl, container);

            // Pager.
            pagerEl = document.createElement('nav');
            pagerEl.className = 'variant-pager';
            pagerEl.setAttribute('aria-label', 'Variant table pagination');
            container.parentNode.insertBefore(pagerEl, container.nextSibling);
            pagerEl.addEventListener('click', function (event) {
                var btn = event.target.closest('.variant-pager__btn');
                if (!btn || btn.disabled) return;
                var page = parseInt(btn.dataset.page, 10);
                if (!page) return;
                state.currentPage = page;
                renderPage();
                container.scrollIntoView({ behavior: 'smooth', block: 'start' });
            });
        }

        // ---- Load all remaining pages via the Section Rendering API ----
        function loadAllVariants() {
            var sectionId = container.dataset.sectionId;
            var productUrl = container.dataset.productUrl;
            var variantsCount = parseInt(container.dataset.variantsCount, 10) || 0;
            var totalPages = Math.ceil(variantsCount / pageSize);
            if (!sectionId || !productUrl || totalPages <= 1) return Promise.resolve();

            // Remove the no-JS native pagination row (JS pagination takes over).
            var nativeRow = tbody.querySelector('.variant-table__native-pagination-row');
            if (nativeRow) nativeRow.remove();

            container.classList.add('variant-table--loading');

            var pages = [];
            for (var p = 2; p <= totalPages; p++) pages.push(p);
            var htmlByPage = {};

            function fetchPage(page) {
                var url = productUrl + (productUrl.indexOf('?') === -1 ? '?' : '&') +
                    'section_id=' + encodeURIComponent(sectionId) + '&page=' + page;
                return fetch(url, { credentials: 'same-origin' })
                    .then(function (r) { return r.ok ? r.text() : ''; })
                    .then(function (html) {
                        if (!html) return;
                        var doc = new DOMParser().parseFromString(html, 'text/html');
                        var rows = doc.querySelectorAll('.product-variant-table tbody .variant-row');
                        var out = [];
                        rows.forEach(function (row) { out.push(row.outerHTML); });
                        htmlByPage[page] = out.join('');
                    })
                    .catch(function () { /* skip failed page; others still load */ });
            }

            // Bounded-concurrency worker pool.
            function runPool() {
                var idx = 0;
                function next() {
                    if (idx >= pages.length) return Promise.resolve();
                    var page = pages[idx++];
                    return fetchPage(page).then(next);
                }
                var workers = [];
                for (var w = 0; w < Math.min(CONCURRENCY, pages.length); w++) workers.push(next());
                return Promise.all(workers);
            }

            return runPool().then(function () {
                var combined = pages.map(function (p) { return htmlByPage[p] || ''; }).join('');
                if (combined && noResults) {
                    noResults.insertAdjacentHTML('beforebegin', combined);
                } else if (combined) {
                    tbody.insertAdjacentHTML('beforeend', combined);
                }
                container.classList.remove('variant-table--loading');
            });
        }

        // ---- Wire up ----
        if (needsLoadAll) {
            if (container.dataset.loaded === 'true') return; // idempotent across section reloads
            container.dataset.loaded = 'true';
            buildEnhancedUI();
            loadAllVariants().then(function () {
                sortAllRows();
                buildToggles(); // sets the measurement default; built from the full set
                rebuildFilterOptions(); // after toggles so option filters cascade to the default unit
                applyConfigRequirement(); // covers the rows loaded via the Section Rendering API
                applyFilters();
                publishVariantMatrix();
            });
        } else {
            // <=250 products: rows already complete in the DOM; build toggles immediately.
            // Sort here too. The Liquid path emits rows in raw Shopify variant order —
            // it collects group values by first appearance and applies no ordering of
            // its own — so without this the table simply mirrors whatever the import
            // script wrote, which is how a correct table and a wrong one ended up
            // living side by side depending only on variant count.
            sortAllRows();
            buildToggles();
            rebuildFilterOptions(); // canonical option ordering + measurement cascade
            applyConfigRequirement();
            // Apply the default unit (Inch) on load when a measurement toggle exists.
            if (MEASUREMENT_KEY in state.toggleDefaults) applyFilters();
            // ...and unconditionally here, because a table with no measurement
            // toggle never calls applyFilters() on load and would otherwise render
            // all 200+ cards on the first paint.
            applyMobileCap(true);
        }
    }

    /**
     * Publish the full variant matrix so the main product picker can replace its
     * (250-capped) cascade matrix and stop hiding valid past-250 option values.
     */
    function publishVariantMatrix() {
        var rows = document.querySelectorAll('.product-variant-table tbody .variant-row');
        if (!rows.length) return;
        var variants = [];
        rows.forEach(function (row) {
            var options = [row.dataset.option1, row.dataset.option2, row.dataset.option3]
                .filter(function (v) { return v !== undefined && v !== ''; });
            variants.push({
                id: parseInt(row.dataset.variantId, 10),
                options: options,
                available: row.dataset.available === 'true',
                measurement: row.getAttribute('data-meta-measurement') || null,
                // Add-on tiers ride along so product-addons.js can resolve
                // variants past Liquid's 250 cap (its own JSON map is capped).
                handleTier: row.getAttribute('data-addon-handle-tier') || null,
                lfcTier: row.getAttribute('data-addon-lfc-tier') || null,
            });
        });
        // Cache globally so product-info.js can adopt it even if it connects late.
        window.variantTableFullMatrix = variants;
        document.dispatchEvent(new CustomEvent('variant-table:loaded', { detail: { variants: variants } }));
    }

    /**
     * Updates the main product selection to the clicked variant
     * and scrolls to the product info section.
     */
    function selectVariant(variantId) {
        var table = document.querySelector('.product-variant-table');
        var row = table ? table.querySelector('[data-variant-id="' + variantId + '"]') : null;
        if (!row) return;

        var optionValues = [row.dataset.option1, row.dataset.option2, row.dataset.option3].filter(Boolean);

        var productInfo = document.querySelector('product-info');
        if (!productInfo) return;

        var selects = productInfo.querySelectorAll('variant-selects select.select__select');
        selects.forEach(function (select, i) {
            if (optionValues[i] !== undefined) {
                select.value = optionValues[i];
                select.dispatchEvent(new Event('change', { bubbles: true }));
            }
        });

        productInfo.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    /**
     * On MIXED-price products, if we landed on a call-for-quote variant without an
     * explicit ?variant= deep link, pre-select the first purchasable variant.
     */
    function autoSelectPurchasableDefault() {
        var productInfo = document.querySelector('product-info[data-default-purchasable-variant-id]');
        if (!productInfo) return;
        var variantId = productInfo.dataset.defaultPurchasableVariantId;
        if (!variantId) return;
        if (new URLSearchParams(window.location.search).has('variant')) return;
        if (!productInfo.classList.contains('product-info--quote-only')) return;

        var table = document.querySelector('.product-variant-table');
        var row = table ? table.querySelector('[data-variant-id="' + variantId + '"]') : null;
        if (!row) return;

        var optionValues = [row.dataset.option1, row.dataset.option2, row.dataset.option3].filter(Boolean);
        var selects = productInfo.querySelectorAll('variant-selects select.select__select');
        selects.forEach(function (select, i) {
            if (optionValues[i] !== undefined) {
                select.value = optionValues[i];
                select.dispatchEvent(new Event('change', { bubbles: true }));
            }
        });
    }

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', function () {
            initVariantTable();
            autoSelectPurchasableDefault();
        });
    } else {
        initVariantTable();
        autoSelectPurchasableDefault();
    }

    document.addEventListener('shopify:section:load', initVariantTable);
})();
